import clsx from "clsx";
import get from "lodash/get";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import ExclamationCircle from "~/components/Icons/svg/exclamationCircle";
import PdfIcon from "~/components/Icons/svg/pdf";
import { useGetGuideFileQuery } from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { useCopyText } from "~/shared/hooks/useCopyText";

import s from "./Footer.module.less";

export const Footer: FC = () => {
  const currentYear = new Date().getFullYear();
  const { hasPermission } = usePermissions();
  const { copyText } = useCopyText();
  const { t } = useTranslation();

  // Fetch guide file unconditionally — "Руководство пользователя"
  // must render for everyone (PASDILER-2003). Admin guide still gated by permission below.
  const { data } = useGetGuideFileQuery({
    fetchPolicy: "cache-and-network",
  });

  const guideFile = get(data, "UserGuideGetter.getGuideFile", {});

  const currentLang = useMemo(
    () => localStorage.getItem("language") || "ru",
    [],
  );

  const handleChangeLanguage = useCallback(
    (lang: string) => {
      if (currentLang !== lang) {
        localStorage.setItem("language", lang);
        window.location.reload();
      }
    },
    [currentLang],
  );

  const contacts = [
    {
      value: "8-800-100-1538",
      title: t("footer.phone"),
      postfix: t("footer.phoneFree"),
    },
    {
      value: "etika@vaz.ru",
      title: t("footer.email"),
      onClick: () => copyText("etika@vaz.ru"),
    },
  ];

  const codex = {
    path: "/userfiles/-2/1766036166786_1706685588618_Кодекс%20этики.pdf",
    downloadTitle: "Кодекс этики.pdf",
  };

  return (
    <footer className={s.footer}>
      <div className={s.content}>
        <div className={s.left}>
          <div className={s.titleRow}>
            <ExclamationCircle fill="#ffffff" />
            <span className={s.title}>{t("footer.ethicsHotline")}</span>
          </div>
          <div className={s.description}>
            <p>{t("footer.ethicsDescription")}</p>
            <ul>
              {contacts.map((el) => (
                <li key={el.title}>
                  {el.title}{" "}
                  <span
                    className={clsx(s.orange, { [s.clickable]: el.onClick })}
                    onClick={el.onClick}
                  >
                    {el.value}
                  </span>{" "}
                  {el.postfix}
                </li>
              ))}
            </ul>
          </div>
          <div className={s.pdfBlock}>
            <a
              href={codex.path}
              className={s.pdfLink}
              download={codex.downloadTitle}
              target="_blank"
              rel="noreferrer"
            >
              <PdfIcon />
              <div className={s.pdfText}>
                <span className={s.pdfTitle}>{t("footer.ethicsCode")}</span>
                <span className={s.pdfSubtitle}>{t("footer.downloadPdf")}</span>
              </div>
            </a>
          </div>
        </div>

        <div className={s.right}>
          <div className={s.languages}>
            <span
              className={clsx(s.lang, { [s.langActive]: currentLang === "ru" })}
              onClick={() => handleChangeLanguage("ru")}
            >
              РУС
            </span>
            <span
              className={clsx(s.lang, { [s.langActive]: currentLang === "en" })}
              onClick={() => handleChangeLanguage("en")}
            >
              ENG
            </span>
          </div>
          <div className={s.bottomRight}>
            <div className={s.guideLinks}>
              {hasPermission("user_guides.getGuideFile") &&
                guideFile?.AdminFile && (
                  <a
                    target="_blank"
                    href={guideFile?.AdminFile?.link}
                    className={s.guideLink}
                    rel="noreferrer"
                  >
                    {t("footer.adminGuide")}
                  </a>
                )}
              {guideFile?.UserFile && (
                <a
                  target="_blank"
                  href={guideFile?.UserFile?.link}
                  className={s.guideLink}
                  rel="noreferrer"
                >
                  {t("footer.userGuide")}
                </a>
              )}
            </div>
            <p className={s.copyright}>Copyright &copy; {currentYear} LADA</p>
          </div>
        </div>
      </div>
    </footer>
  );
};
